import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import {
  Modal,
  Button,
  Icon,
  Spin,
  Row,
  Col,
} from 'src/Library/AntdLibComponents';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FileVGMModalStyles } from './FileVGMModal.styles';
import { isValidArray } from 'src/Library/Helper';
import { getOdexStatusAndColor } from './UploadSIModal.utils';
import { RetryIcon } from 'src/components/common/icons/retryIcon';
import { useGenericState } from 'src/Library/UseGenericState';
import { getConditionalBullets } from 'src/Library/Common';

interface IProps extends StylesProps<ReturnType<typeof FileVGMModalStyles>> {
  isVisible: boolean;
  onOkay: (isRetryError?: boolean, isViewRequest?: boolean) => void;
  successResponse;
  openDocumentTab: (shouldOpenDraft?, tabToOpen?) => void;
  handleCreateNewRequest: () => void;
  containerCounts;
  isFetchingContainerCount?: boolean;
  handleRetryRequest: (requestBody) => void;
  requestBody;
  openTasksTab: () => void;
  formType: string;
}

interface StateProps {
  isConfirmationModalVisible?: boolean;
}

const FileOdexSuccessModal = (props: IProps): React.ReactElement => {

  const {
    classes,
    isVisible,
    onOkay,
    successResponse,
    openDocumentTab,
    handleCreateNewRequest,
    containerCounts,
    isFetchingContainerCount,
    handleRetryRequest,
    requestBody,
    openTasksTab,
    formType,
  } = props;

  const [state, setState] = useGenericState<StateProps>({});

  const {
    isConfirmationModalVisible,
  } = state;

  const isForm13 = formType === 'FORM13';
  const containerDetails = isForm13 ? successResponse?.containerDetails : successResponse?.weighmentDetailsFiled

  const renderTitle = () : React.ReactElement => {
    const formLabel = isForm13 ? 'Form 13' : 'eVGM';
    return (
      <div className={classes.successHeader} style={{ display: 'flex' }}>
        {isValidArray(containerDetails) ?
        <React.Fragment>
          <Icon type="check-circle" style={{ color: '#52C41A', marginRight: '8px', fontSize: '18px' }}
            theme="filled" />
          <span>
            {formLabel}&nbsp;
            {successResponse?.referenceNumber &&
            <span> with ODeX # {successResponse?.referenceNumber}</span>}
            &nbsp;has been successfully submitted.
            The details will be updated after successful processing.
          </span>
        </React.Fragment> :
        <React.Fragment>
          <Icon type="close-circle" style={{ color: '#F5222D', marginRight: '8px', fontSize: '18px' }}
            theme="filled" />
          <span>
            This {formLabel} request couldn’t be submitted for the following.
            Please rectify these and submit a new request.
          </span>
        </React.Fragment>}
      </div>
    );
  };

  const getRetryRequestBody = () => {
    if (isForm13) {
      return requestBody;
    }
    const failedContainers = successResponse?.failureArray?.map(item => item.containerNumber);
    const body = {
      ...requestBody,
      containerWeighmentDetails: requestBody?.weighmentDetails?.filter(item =>
        failedContainers?.indexOf(item.containerNumber) !== -1),
      authorisedPerson: {
        designation: requestBody?.authorisedPersonDesignation,
        id: requestBody?.authorisedPersonId,
        mobileno: requestBody?.authorisedPersonMobileno,
        name: requestBody?.authorisedPersonName,
      },
      authorisedPersonId: requestBody?.authorisedPersonId,
    }
    return body;
  }

  const renderSuggestedActions = () : React.ReactElement => {
    return (
      <div style={{ padding: '16px 0', borderTop: '1px solid rgba(0,0,0,0.09)', marginTop: '20px' }}>
        <div style={{ fontWeight: 600, color: '#424242', fontSize: '12px' }}>Suggested Actions</div>
        <div className={classes.suggestedActionsDiv}>
          {isValidArray(successResponse?.failureArray) && <div
            onClick={() => {
              onOkay(true);
              handleRetryRequest(getRetryRequestBody());
            }}
            className={classes.suggestedActions}
          >
            <RetryIcon style={{ marginRight: '6px' }} />
            Retry Errors
          </div>}
          {isFetchingContainerCount ? <Spin size="small" /> : (containerCounts?.[formType]?.pendingContainerCount > 0 && <div
            className={classes.suggestedActions}
            onClick={() => {
              onOkay();
              handleCreateNewRequest();
            }}
            style={{ marginLeft: '12px' }}
          >
            <Icon type="plus" style={{ color: '#1890FF', marginRight: '6px' }} />
            Create New Request
          </div>)}
          {isValidArray(containerDetails) && <div
            className={classes.suggestedActions}
            style={{ marginLeft: '12px' }}
            onClick={() => {
              openDocumentTab(false, formType);
              onOkay(false, true);
            }}
          >
            <Icon type="file-text" style={{ color: '#1890FF', marginRight: '6px' }} />
            View Request
          </div>}
        </div>
      </div>
    );
  };

  const renderVGMFailedContainers = () : React.ReactElement => {
    return (
      <div className={classes.filedContainersDiv}>
        {successResponse?.failureArray?.map((item, index) =>
        <div style={{ padding: '10px', borderTop: index === 0 ? 0 : '1px solid #D9D9D9' }}>
          <Icon type="close-circle" style={{ color: '#F5222D', marginRight: '6px' }} theme="filled" />
          Container <span style={{ fontWeight: 600 }}>{item?.containerNumber}</span> have the following error(s)
          <ul style={{ paddingLeft: '14%', marginBottom: 0 }}>
            {item?.reason?.map((reason) => {
              return (
              <li>{reason}</li>
              )
            })}
          </ul>
        </div>)}
      </div>
    );
  };

  const renderForm13FailedErrors = () : React.ReactElement => {
    return (
      <div className={classes.filedContainersDiv}>
        {getConditionalBullets(successResponse?.failureArray)}
      </div>
    );
  };

  const renderContainersStatus = () : React.ReactElement => {
    return (
      <div>
        <Row style={{
          padding: '10px',
          border: '1px solid #D9D9D9',
          borderRadius: '4px 4px 0 0',
          borderBottom: 0,
        }}>
          <Col span={12} style={{ fontWeight: 600 }}>Container Number</Col>
          <Col span={12} style={{ fontWeight: 600 }}>Status</Col>
        </Row>
        <div className={classes.filedContainersDiv}
          style={{ marginBottom: 0, borderRadius: '0 0 4px 4px' }}
        >
          {containerDetails?.map((item, index) =>
          <Row style={{ padding: '10px', borderTop: index === 0 ? 0 : '1px solid #D9D9D9' }}>
            <Col span={12} style={{ fontWeight: 600 }}>{item?.containerNumber}</Col>
            <Col span={12} style={{ fontWeight: 600 }}>
              <div
                className={classes.dot}
                style={{ backgroundColor: getOdexStatusAndColor(item?.status)?.color, marginRight: '8px' }}
              />
              {getOdexStatusAndColor(item?.status)?.label}
            </Col>
          </Row>)}
        </div>
      </div>
    );
  };

  const renderConfirmationModal = () => {
    return (
      <Modal
        closable={false}
        className={classes.confirmationModal}
        width={366}
        onCancel={() => {
          onOkay();
          setState({ isConfirmationModalVisible: false })
          openTasksTab();
        }}
        zIndex={3100}
        visible={isConfirmationModalVisible}
        destroyOnClose
        footer={null}>
        <div style={{
          textAlign: 'center', padding: '0px 24px 24px 24px', fontWeight: 600, fontSize: '16px'
        }}>
          <div>
            <Icon type="exclamation-circle" theme="filled"
              style={{ color: '#FAB826', fontSize: '64px' }} />
          </div>
          <div style={{ color: '#262626', marginTop: '16px' }}>
            There are
            still {containerCounts?.[formType]?.pendingContainerCount}
            /{containerCounts?.[formType]?.totalContainerCount} containers
            for which request haven’t been submitted.
          </div>
          <div style={{ color: '#1875F0', marginTop: '12px', cursor: 'pointer' }}
            onClick={() => {
              onOkay();
              setState({ isConfirmationModalVisible: false })
              handleCreateNewRequest();
            }}
          >Create a new request</div>
          <div style={{ color: '#666666', marginTop: '12px', cursor: 'pointer', fontSize: '14px' }}
            onClick={() => {
              onOkay();
              setState({ isConfirmationModalVisible: false })
              openTasksTab();
            }}
          >
            Take me to Tasks
          </div>
        </div>
      </Modal>
    );
  };

  const handleOkay = () => {
    if (containerCounts?.[formType]?.pendingContainerCount > 0) {
      setState({ isConfirmationModalVisible: true })
    } else {
      onOkay()
    }
  }

  return (
    <Modal
      visible={isVisible}
      zIndex={3000}
      width={450}
      className={classNames(classes.successModal, classes.successModalBody)}
      title={renderTitle()}
      onCancel={() => handleOkay()}
      closable={false}
      footer={
        <Button
          className={classes.successButton}
          onClick={() => handleOkay()}
        >
          Okay
        </Button>
      }
    >
      {isValidArray(successResponse?.failureArray) &&
        isForm13 ? renderForm13FailedErrors() : renderVGMFailedContainers()}
      {isValidArray(containerDetails) && renderContainersStatus()}
      {renderSuggestedActions()}
      {isConfirmationModalVisible && renderConfirmationModal()}
    </Modal>
  );
};

const mapStateToProps = ({ shipmentExecutionReducer }, ownProps) => ({
  containerCounts: shipmentExecutionReducer.siPendingCount,
  isFetchingContainerCount: shipmentExecutionReducer.siPendingCount?.isFetchingContainerCount,
});


export default connect(
  mapStateToProps,
  null,
)(React.memo(withStyles(FileVGMModalStyles)(FileOdexSuccessModal)));
